import * as React from 'react'
import { Text, View, ScrollView, TouchableOpacity, Platform } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'
import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Button from '@components/button/Button'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import IncidentCard from '@components/incidentCard/IncidentCard'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import SimpleAlert from '@components/modal/SimpleAlert'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'
import { findTenant, findManifestation } from './utils'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as IncidentStore from '@store/incident/incident'
import * as LocationStore from '@store/location/location'
import * as AuthStore from '@store/auth/auth'
import * as UsersStore from '@store/users/users'
import * as EDLStore from '@store/edl/edl'

import { findLocalisationSubLevels } from '@screens/incident/utils'
import { AuthScope, EtatDesLieuxTypeEnum } from '@api/enum'

import config from '@app/config'
import exporter from '@utils/exporter'
import Logger from '@utils/logger'
import api from '@api/api'
import Modal from '@components/modal/Modal'
import Loader from '@components/loader/Loader'

type Section = { title: string; data: IncidentResponse[] }

interface Props {
  id: string
}

const EDLScreen = ({ id }: Props) => {
  const EDLs = useReducer(EDLStore.store, (s) => s.EDLs)

  const [edl, setEdl] = React.useState<EtatDesLieuxResponse>()

  const i18n = useI18n()
  const navigation = useNavigation()

  React.useEffect(() => {
    const found = EDLs.find((e) => e.id === id)

    if (found) {
      setEdl(found)
    } else {
      api.edl
        .get(id)
        .then(setEdl)
        .catch(() =>
          SimpleAlert.open({
            title: i18n.t('screens.edlRecap.errors.title'),
            description: i18n.t('screens.edlRecap.errors.description'),
            buttons: [
              {
                label: i18n.t('screens.edlRecap.errors.button'),
                onClick: () => {
                  SimpleAlert.close()
                  navigation.jumpTo('/edl')
                },
              },
            ],
          })
        )
    }
  }, [id])

  if (!edl) {
    return <Root loading />
  }

  return <EDLDetail edl={edl} />
}

interface DetailProps {
  edl: EtatDesLieuxResponse
}

// edl rule
// in -> accepted = all incident, refused = empty
// out -> accepted = orga incident, refused = viparis incident
const EDLDetail = ({ edl }: DetailProps) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const user = useReducer(UserStore.store, (s) => s.user!)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const users = useReducer(UsersStore.store, (s) => s.users)

  const places = useReducer(LocationStore.store, (s) => s.places)
  const spaces = React.useMemo(() => places.filter((p) => !p.parentId), [places])

  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)

  const [open, setOpen] = React.useState(true)
  const [tab, setTab] = React.useState<'ORGA' | 'VIPARIS'>('ORGA')
  const [exportingPDF, setExportingPDF] = React.useState(false)
  const [exportingXLSX, setExportingXLSX] = React.useState(false)
  const [synchronizing, setSynchronizing] = React.useState(false)
  const [author, setAuthor] = React.useState<UserBasicResponse | UserResponse>()

  const manif = React.useMemo(() => findManifestation(manifestations, edl.manifestationId), [manifestations, edl])
  const tenant = React.useMemo(() => findTenant(tenants, edl.locatairePermanentId), [tenants, edl])
  const name = !!manif ? manif.libelle : !!tenant ? tenant.tenant.nomLocatairePermanent : ''

  const edlIncidents = React.useMemo(
    () =>
      incidents
        .filter((i) => edl.incidentAccepteIds.includes(i.id) || edl.incidentRefuseIds.includes(i.id))
        .sort((a, b) => b.dateDeCreation.localeCompare(a.dateDeCreation)),
    [incidents, edl]
  )

  const incidentsOrga = React.useMemo(
    () => edlIncidents.filter((i) => edl.incidentAccepteIds.includes(i.id)),
    [edlIncidents, edl]
  )

  const incidentsViparis = React.useMemo(
    () => edlIncidents.filter((i) => edl.incidentRefuseIds.includes(i.id)),
    [edlIncidents, edl]
  )

  const formSpaces = React.useMemo(
    () => spaces.filter((p) => edl.espaceIds.includes(p.id)).sort((a, b) => a.libelle.localeCompare(b.libelle)),
    [edl, spaces]
  )

  const tabs: { label: string; value: 'ORGA' | 'VIPARIS' }[] = [
    { label: i18n.t('screens.edlRecap.incidentsOrganisatorTab', { number: incidentsOrga.length }), value: 'ORGA' },
    { label: i18n.t('screens.edlRecap.incidentsViparisTab', { number: incidentsViparis.length }), value: 'VIPARIS' },
  ]

  const headerLinks = [
    { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
    { label: i18n.t(`screens.edl.title`), onClick: () => navigation.push(`/edl`) },
  ]

  const downloadEDL = () => {
    SimpleAlert.open({
      title: i18n.t('screens.edlRecap.popup.title'),
      description: i18n.t('screens.edlRecap.popup.description'),
      buttons: [
        {
          label: i18n.t('screens.edlRecap.popup.pdf'),
          onClick: () => {
            SimpleAlert.close()
            setExportingPDF(true)
          },
          disabled: exportingPDF,
        },
        {
          label: i18n.t('screens.edlRecap.popup.xls'),
          onClick: () => {
            SimpleAlert.close()
            setExportingXLSX(true)
          },
          disabled: exportingXLSX,
        },
      ],
    })
  }

  const sync = () => {
    setSynchronizing(true)

    api.edl
      .sync(edl.id)
      .then(() =>
        SimpleAlert.open({
          title: i18n.t('screens.edlRecap.sync.title'),
          description: i18n.t('screens.edlRecap.sync.success'),
          buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
        })
      )
      .catch((err) =>
        SimpleAlert.open({
          title: i18n.t('screens.edlRecap.sync.title'),
          description: i18n.t(`screens.edlRecap.sync.${err.code === 409 ? 'already' : 'failed'}`),
          buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
        })
      )
      .finally(() => setSynchronizing(false))
  }

  React.useEffect(() => {
    const ids = incidents.map((i) => i.id)
    const edlIncIds = [...edl.incidentAccepteIds, ...edl.incidentRefuseIds]
    const missing = edlIncIds.filter((id) => !ids.includes(id))

    if (missing.length > 0) {
      // download every missing incidents from opened
      Promise.all(missing.map(api.incident.get))
        .then((res) => IncidentStore.actions.setIncidents([...incidents, ...res]))
        .catch(Logger.error)
    }
  }, [edl, incidents.length])

  React.useEffect(() => {
    const found = users.find((u) => u.id === edl.utilisateurInitiateurId)

    if (found) {
      setAuthor(found)
    } else {
      api.users.get(edl.utilisateurInitiateurId).then(setAuthor).catch(Logger.error)
    }
  }, [edl, users])

  React.useEffect(() => {
    if (exportingPDF) {
      const { tokens } = AuthStore.store.getState()

      if (tokens) {
        if (isPhone) {
          Modal.open({
            Content: () => <Loader />,
            position: 'center',
          })
        }
        exporter
          .download<EtatDesLieuxPDFRequest>(config.ws.edl.downloadPDF, `export_edl.pdf`, 'application/pdf', {
            etatDesLieuxIds: [edl.id],
          })
          .then(() => {
            if (isPhone) {
              SimpleAlert.open({
                title: i18n.t('screens.edlRecap.export.success.title'),
                description: i18n.t('screens.edlRecap.export.success.description'),
                buttons: [{ label: i18n.t('screens.edlRecap.export.success.ok'), onClick: () => SimpleAlert.close() }],
              })
            }
          })
          .catch((error) => {
            Logger.error('Unable to download', error)
            SimpleAlert.open({
              title: i18n.t('screens.edlRecap.export.error.title'),
              description: i18n.t('screens.edlRecap.export.error.description'),
              buttons: [{ label: i18n.t('screens.edlRecap.export.error.ok'), onClick: () => SimpleAlert.close() }],
            })
          })
          .finally(() => {
            if (isPhone) {
              Modal.close()
            }
            setExportingPDF(false)
          })
      }
    }
  }, [exportingPDF])

  React.useEffect(() => {
    if (exportingXLSX) {
      const { tokens } = AuthStore.store.getState()

      if (tokens) {
        if (isPhone) {
          Modal.open({
            Content: () => <Loader />,
            position: 'center',
          })
        }
        exporter
          .download<EtatDesLieuxExportRequest>(
            config.ws.edl.downloadXLSX,
            `export_edl.xlsx`,
            'application/vnd.ms-excel',
            { etatDesLieuxIds: [edl.id] }
          )
          .then(() => {
            if (isPhone) {
              SimpleAlert.open({
                title: i18n.t('screens.edlRecap.export.success.title'),
                description: i18n.t('screens.edlRecap.export.success.description'),
                buttons: [{ label: i18n.t('screens.edlRecap.export.success.ok'), onClick: () => SimpleAlert.close() }],
              })
            }
          })
          .catch((error) => {
            Logger.error('Unable to download', error)
            SimpleAlert.open({
              title: i18n.t('screens.edlRecap.export.error.title'),
              description: i18n.t('screens.edlRecap.export.error.description'),
              buttons: [{ label: i18n.t('screens.edlRecap.export.error.ok'), onClick: () => SimpleAlert.close() }],
            })
          })
          .finally(() => {
            if (isPhone) {
              Modal.close()
            }
            setExportingXLSX(false)
          })
      }
    }
  }, [exportingXLSX])

  const headerButtons: HeaderButton[] = scopes.includes(AuthScope.EdlTelechargementLecture)
    ? scopes.includes(AuthScope.EdlSetCommandeMagicRenvoi) && edl.envoieCommandeMagic === false && Platform.OS === 'web'
      ? [
          {
            label: i18n.t('screens.edlRecap.download'),
            onClick: downloadEDL,
            leftIcon: 'download',
          },
          {
            label: i18n.t('screens.edlRecap.sync.button'),
            onClick: sync,
            status: synchronizing ? 'loading' : 'active',
          },
        ]
      : [
          {
            label: i18n.t('screens.edlRecap.download'),
            onClick: downloadEDL,
            leftIcon: 'download',
          },
        ]
    : []

  const sections: Section[] = React.useMemo(
    () =>
      formSpaces.map((s) => {
        const locations = [s.id, ...findLocalisationSubLevels(places, s).map((l) => l.id)]

        return {
          title: s.libelle,
          data: edlIncidents.filter(
            (inc) => inc.localisationId && (inc.localisationId === s.id || locations.includes(inc.localisationId))
          ),
        }
      }),
    [formSpaces, places, edlIncidents]
  )

  const renderItem = (item: IncidentResponse) => {
    return (
      <IncidentContainer
        key={item.id}
        activeOpacity={0.9}
        onPress={() => navigation.push(`/incidentDetail/${item.id}`)}>
        <IncidentCard
          incident={item}
          displayPrice={edl.type === EtatDesLieuxTypeEnum.SORTIE && user.typeUtilisateur === 'VIPARIS'}
        />
      </IncidentContainer>
    )
  }

  const renderSpace = (section: Section) => {
    return (
      <PlaceContainer key={section.title}>
        <PlaceName>
          {i18n.t('screens.edlRecap.placeName', { name: section.title, number: section.data.length })}
        </PlaceName>

        <IncidentsList>{section.data.map(renderItem)}</IncidentsList>
      </PlaceContainer>
    )
  }

  const renderData = (sections: Section[]) => {
    // if exit edl, split between organizer and viparis
    if (edl.type === EtatDesLieuxTypeEnum.SORTIE) {
      const orgaSections = sections
        .map((s) => ({ ...s, data: s.data.filter((i) => edl.incidentAccepteIds.includes(i.id)) }))
        .filter((s) => s.data.length > 0)
      const orgaSize = orgaSections.reduce((acc, cur) => acc + cur.data.length, 0)

      const viparisSections = sections
        .map((s) => ({ ...s, data: s.data.filter((i) => edl.incidentRefuseIds.includes(i.id)) }))
        .filter((s) => s.data.length > 0)
      const viparisSize = viparisSections.reduce((acc, cur) => acc + cur.data.length, 0)

      if (!isPhone) {
        return (
          <>
            <SubSection>
              <SubSectionTitleContainer>
                <SectionTitle title={i18n.t('screens.edlRecap.incidentsOrganisator', { number: orgaSize })} />
              </SubSectionTitleContainer>
              {orgaSections.map(renderSpace)}
            </SubSection>

            <SubSection>
              <SubSectionTitleContainer>
                <SectionTitle title={i18n.t('screens.edlRecap.incidentsViparis', { number: viparisSize })} />
              </SubSectionTitleContainer>
              {viparisSections.map(renderSpace)}
            </SubSection>
          </>
        )
      }

      return (
        <>
          <TabContainer>
            <SegmentedControl
              data={tabs}
              value={tabs.find((t) => t.value === tab)}
              onItemSelected={(opt) => setTab(opt.value)}
            />
          </TabContainer>

          {tab === 'ORGA' ? orgaSections.map(renderSpace) : viparisSections.map(renderSpace)}
        </>
      )
    }

    return sections.map(renderSpace)
  }

  const renderIncidents = () => {
    const size = edlIncidents.length
    const name = isPhone
      ? edl.type === EtatDesLieuxTypeEnum.ENTREE
        ? 'incidentsTitlePhone'
        : 'newIncidentsPhone'
      : edl.type === EtatDesLieuxTypeEnum.ENTREE
      ? 'incidentsTitle'
      : 'newIncidents'

    return (
      <>
        <SectionContainer activeOpacity={0.8} onPress={() => setOpen(!open)} disabled={!isPhone}>
          <SectionTitleContainer>
            <SectionTitle title={i18n.t(`screens.edlRecap.${name}`, { number: size })} />
          </SectionTitleContainer>

          {isPhone && (
            <>
              <TagIncident>
                <PlaceName noGrey>{size}</PlaceName>
              </TagIncident>
              <ArrowContainer>
                <Icon name={open ? 'arrow-up' : 'arrow-down'} color={theme.colors.primary} />
              </ArrowContainer>
            </>
          )}
        </SectionContainer>

        {(!isPhone || open) && renderData(sections)}
      </>
    )
  }

  const renderBottomButton = () => {
    return (
      <BottomButton>
        <ButtonContainer>
          <Button
            textOnly
            label={i18n.t('screens.edl.spaces.back')}
            onClick={() => navigation.jumpTo('/edl')}
            status="active"
          />
        </ButtonContainer>
      </BottomButton>
    )
  }

  const renderComment = () => (
    <CommentContainer>
      <SectionTitle title={i18n.t('screens.edlRecap.comment')} />
      <InputContainer>
        <Comment>{edl.commentaire}</Comment>
      </InputContainer>
    </CommentContainer>
  )

  const renderContact = (name: string, job?: string, email?: string) => {
    if (bp === 'phone') {
      return (
        <>
          <ContactInfosContainer paddingTop={0}>
            <CreationDate color={theme.colors.primaryText} bold body>
              {name}
            </CreationDate>
            <Icon name="email" size={20} color={theme.colors.primary} />
          </ContactInfosContainer>

          {!!job && <InfosBold grey>{job}</InfosBold>}
        </>
      )
    }

    return (
      <>
        <ContactInfosContainer paddingTop={32}>
          <CreationDate color={theme.colors.primaryText} bold>
            {name}
          </CreationDate>
          {!!job && (
            <CreationDate color={theme.colors.secondaryText}>
              {i18n.t('screens.edlRecap.contactRole', { role: job })}
            </CreationDate>
          )}
        </ContactInfosContainer>

        {!!email && (
          <ContactInfosContainer paddingTop={16}>
            <IconContainer>
              <Icon name="email" size={20} color={theme.colors.primary} />
            </IconContainer>

            <CreationDate color={theme.colors.primaryText}>{email}</CreationDate>
          </ContactInfosContainer>
        )}
      </>
    )
  }

  const renderHeader = () => {
    const date = new Date(edl.dateDeValidation)

    return (
      <HeaderContainer>
        <InformationsContainer>
          <Row column={bp !== 'big'}>
            <InfosBold>
              {i18n.t(`screens.edlRecap.recapInProgressTitle`, { type: i18n.t(`screens.edlRecap.${edl.type}`) })}
            </InfosBold>
            <CreationDate color={theme.colors.secondaryText}>
              {i18n.t('screens.edlRecap.creationDate', { date })}
            </CreationDate>
          </Row>

          <ManifestationName>{name}</ManifestationName>

          {bp === 'phone' && <Separator />}

          <SpaceName>{formSpaces.map((s) => s.libelle).join(', ')}</SpaceName>
        </InformationsContainer>

        <ContactContainer>
          <SectionTitle title={i18n.t('screens.edlRecap.contact')} />

          {edl.utilisateurInitiateurId === user.id
            ? renderContact(`${user.prenom} ${user.nom}`, user.fonction, user.email)
            : !!author
            ? renderContact(
                `${author.prenom} ${author.nom}`,
                (author as UserResponse).fonction,
                (author as UserResponse).email
              )
            : null}

          {!!edl.contactOrganisateur.email &&
            renderContact(
              `${edl.contactOrganisateur.prenom} ${edl.contactOrganisateur.nom}`,
              name,
              edl.contactOrganisateur.email
            )}
        </ContactContainer>
      </HeaderContainer>
    )
  }

  return (
    <Root
      onLeft={() => navigation.jumpTo('/edl')}
      header={i18n.t('screens.edl.title')}
      renderFixedBottom={renderBottomButton}
      right="download"
      onRight={downloadEDL}
      fixed>
      <ScrollContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
        {!isPhone && (
          <Header title={i18n.t('screens.edlRecap.stepTitle')} links={headerLinks} buttons={headerButtons} />
        )}
        <MainHeaderContainer>{renderHeader()}</MainHeaderContainer>

        {renderIncidents()}

        {renderComment()}
      </ScrollContainer>
    </Root>
  )
}

export default EDLScreen

const ScrollContainer = styled(ScrollView)`
  flex: 1;
  flex-direction: column;
`

const MainHeaderContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'padding: 24px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 0px 16px 16px;'};
  background-color: ${(props) =>
    props.theme.breakpoint !== 'phone' ? props.theme.colors.background : props.theme.colors.primary};
`

const HeaderContainer = styled(View)`
  ${(props) => props.theme.breakpoint === 'phone' && 'margin-top: 16px;'};
  margin-bottom: ${(props) => (props.theme.breakpoint !== 'phone' ? '24' : '16')}px;
  background-color: ${(props) =>
    props.theme.breakpoint !== 'phone' ? props.theme.colors.incident : props.theme.colors.background};
  border-radius: 8px;
  flex-direction: ${(props) => (props.theme.breakpoint !== 'big' ? 'column' : 'row')};
`

const InformationsContainer = styled(View)`
  ${(props) => props.theme.breakpoint === 'big' && 'flex: 1;'};
  ${(props) => props.theme.breakpoint !== 'phone' && 'padding: 24px 32px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 16px;'};
`

const ContactContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'width: 360px;'};
  background-color: ${(props) =>
    props.theme.breakpoint !== 'phone' ? props.theme.colors.background : props.theme.colors.incident};
  border-radius: 8px;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin: 8px 34px 34px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'margin: 8px 16px 16px;'};
  ${(props) => props.theme.breakpoint !== 'phone' && 'padding: 32px 24px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 16px 16px 70px;'};
`

const InfosBold = styled(Text)<{ grey?: boolean }>`
  ${(props) => (props.theme.breakpoint !== 'phone' ? props.theme.fonts.Headline : props.theme.fonts.Body)};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding-bottom: 8px'};
  ${(props) => props.grey && `color: ${props.theme.colors.secondaryText};`}
`

const ManifestationName = styled(Text)`
  ${(props) => (props.theme.breakpoint !== 'phone' ? props.theme.fonts.Heading1 : props.theme.fonts.Title3)};
  ${(props) => props.theme.breakpoint === 'phone' && 'padding: 16px 0px;'}
  ${(props) => props.theme.breakpoint !== 'phone' && 'padding: 16px 0px 8px;'}
  ${(props) => props.theme.breakpoint === 'phone' && '  text-align: center;'}
`

const SpaceName = styled(Text)`
  ${(props) => (props.theme.breakpoint !== 'phone' ? props.theme.fonts.Body : props.theme.fonts.Caption1)};
  color: ${(props) =>
    props.theme.breakpoint !== 'phone' ? props.theme.colors.secondaryText : props.theme.colors.primaryText};
`

const CreationDate = styled(Text)<{ color: string; bold?: boolean; body?: boolean }>`
  ${(props) =>
    props.body
      ? props.theme.fonts.Body
      : props.theme.breakpoint === 'phone'
      ? props.theme.fonts.Caption1
      : props.bold
      ? props.theme.fonts.Callout
      : props.theme.fonts.Body};
  color: ${(props) => props.color};
`

const Row = styled(View)<{ column?: boolean }>`
  flex-direction: ${(props) => (props.column ? 'column' : 'row')};
  ${(props) => (!props.column || props.theme.breakpoint === 'phone') && 'align-items: center;'};
  ${(props) => !props.column && 'justify-content: space-between;'};
`

const ContactInfosContainer = styled(View)<{ paddingTop: number }>`
  flex-direction: row;
  padding-top: ${(props) => props.paddingTop}px;
  align-items: center;
  ${(props) => props.theme.breakpoint === 'phone' && 'justify-content: space-between;'};
`

const IconContainer = styled(View)`
  padding-right: 10px;
`

const Separator = styled(View)`
  height: 2px;
  background-color: ${(props) => props.theme.colors.disable};
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex: 1;'};
  ${(props) => props.theme.breakpoint !== 'phone' && '  margin-left: 16px;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'width: 100%;'};
  ${(props) => props.theme.breakpoint === 'phone' && 'margin-bottom: 16px;'};
`

const BottomButton = styled(View)`
  justify-content: ${(props) => (props.theme.breakpoint === 'phone' ? 'space-between' : 'center')};
  align-items: center;
  flex-direction: row;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  border: 0 solid ${(props) => props.theme.colors.border};
  ${(props) => props.theme.breakpoint !== 'phone' && 'border-top-width: 1px;'};
  width: ${(props) => (props.theme.breakpoint === 'medium' || props.theme.breakpoint === 'phone' ? '200' : '300')}px;
  padding: 16px 48px;
`

const InputContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundTertiary};
  padding: 24px;
  margin: 16px 0;
`

const PlaceContainer = styled(View)`
  padding-top: 16px;
`

const IncidentContainer = styled(TouchableOpacity)`
  padding: 16px;
`

const PlaceName = styled(Text)<{ noGrey?: boolean }>`
  ${(props) => props.theme.fonts.Body};
  ${(props) => !props.noGrey && `color: ${props.theme.colors.secondaryText};`}
  ${(props) => !props.noGrey && `margin: 0px 24px;`}
`

const IncidentsList = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex-wrap: wrap;`}
  padding: 0 8px;
`

const SubSection = styled(View)`
  padding-top: 16px;
`

const SectionContainer = styled(TouchableOpacity)`
  margin: 0 24px;
  margin-top: 5px;
  flex-direction: row;
  align-items: center;
`

const SectionTitleContainer = styled(View)`
  flex: 1;
`

const TagIncident = styled(View)`
  background-color: ${(props) => props.theme.colors.secondaryActive};
  border-radius: 30px;
  height: 32px;
  width: 32px;
  align-items: center;
  justify-content: center;
`

const ArrowContainer = styled(View)`
  padding: 16px;
`

const SubSectionTitleContainer = styled(View)`
  margin: 8px 24px;
  align-items: flex-start;
`

const TabContainer = styled(View)`
  margin: 8px 24px;
`

const CommentContainer = styled(View)`
  padding: 24px;
`

const Comment = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.secondaryText};
`
